import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { getIcon, type IconName } from "@sylviq/ui-react-web/extensions/icons";
import { Link } from "@tanstack/react-router";
import { ChevronDown, Menu } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PRODUCTS, STATUS_LABEL } from "@/features/products/catalog";
import { clearHint, type RememberHint, readHint } from "@/lib/remember";
import {
  MARKETING_LOGIN_PATH,
  MARKETING_NAV,
  MARKETING_SIGNUP_PATH,
} from "../marketing-nav";
import { BrandLockup } from "./BrandLockup";

export interface SiteHeaderProps {
  /** Opens the logged-out navigation drawer. */
  onMenuClick: () => void;
  /** Reflects drawer state on the hamburger for assistive tech. */
  menuOpen?: boolean;
}

/**
 * Logged-out site chrome bar (~64px, sticky). Left: hamburger + SYLVIQ
 * wordmark home link. Right: avatar slot — hidden without a remember hint,
 * otherwise the stored initial opening an account chooser. The chooser only
 * ever links to `/login`; it never routes to `/home` (a logged-out visitor
 * must not be able to jump into the authenticated app).
 */
export function SiteHeader({ onMenuClick, menuOpen = false }: SiteHeaderProps) {
  const [hint, setHint] = useState<RememberHint | null>(() => readHint());
  const [chooserOpen, setChooserOpen] = useState(false);

  useEffect(() => {
    if (!chooserOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setChooserOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [chooserOpen]);

  const forget = () => {
    clearHint();
    setHint(null);
    setChooserOpen(false);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-[var(--border)] bg-[var(--surface)]">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-2 px-4 sm:px-6">
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="md:hidden"
            onClick={onMenuClick}
            aria-label={
              menuOpen ? "Close navigation menu" : "Open navigation menu"
            }
            aria-expanded={menuOpen}
          >
            <Menu className="size-5" aria-hidden="true" />
          </Button>
          <Link
            to="/"
            aria-label="SYLVIQ home"
            className="flex items-center gap-2.5 rounded-lg px-1 py-1"
          >
            <BrandLockup label={null} className="h-11 w-11" />
          </Link>
          <nav
            aria-label="Main"
            className="ml-4 hidden items-center gap-1 md:flex"
          >
            <ProductsMenu />
            {MARKETING_NAV.map((item) =>
              item.to ? (
                <Button
                  key={item.label}
                  asChild
                  size="sm"
                  variant="ghost"
                  className="text-sm"
                >
                  <Link to={item.to}>{item.label}</Link>
                </Button>
              ) : (
                <Button
                  key={item.label}
                  asChild
                  size="sm"
                  variant="ghost"
                  className="text-sm"
                >
                  <a href={item.href}>{item.label}</a>
                </Button>
              ),
            )}
          </nav>
        </div>

        <div className="relative flex items-center gap-2">
          {hint ? (
            <>
              <button
                type="button"
                onClick={() => setChooserOpen((open) => !open)}
                aria-label={`Account: ${hint.email}`}
                aria-haspopup="dialog"
                aria-expanded={chooserOpen}
                className="grid size-9 cursor-pointer place-items-center rounded-full bg-[var(--action)] text-sm font-semibold text-[var(--action-foreground)]"
              >
                <span aria-hidden="true">{hint.initial}</span>
              </button>
              {chooserOpen ? (
                <>
                  <button
                    type="button"
                    aria-label="Close account menu"
                    onClick={() => setChooserOpen(false)}
                    className="fixed inset-0 z-40 cursor-default bg-[var(--overlay)]"
                  />
                  <div
                    role="dialog"
                    aria-label="Choose account"
                    className="absolute top-11 right-0 z-50 w-64 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-3 shadow-lg"
                  >
                    <p className="px-2 pt-1 text-xs text-[var(--muted-foreground)]">
                      Signed out
                    </p>
                    <p className="truncate px-2 pt-0.5 pb-2 text-sm font-medium text-[var(--foreground)]">
                      {hint.email}
                    </p>
                    <div className="grid gap-1.5">
                      <Button
                        asChild
                        size="sm"
                        className="w-full justify-start"
                      >
                        <Link to={MARKETING_LOGIN_PATH}>
                          <span className="truncate">
                            {`Continue as ${hint.email}`}
                          </span>
                        </Link>
                      </Button>
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="w-full justify-start"
                      >
                        <Link to={MARKETING_LOGIN_PATH}>Use another account</Link>
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={forget}
                        className="w-full justify-start"
                      >
                        Forget this email
                      </Button>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <Button
              asChild
              size="sm"
              variant="ghost"
              className="hidden whitespace-nowrap text-sm min-[360px]:inline-flex"
            >
              <Link to={MARKETING_LOGIN_PATH}>Sign in</Link>
            </Button>
          )}
          <Button
            asChild
            size="sm"
            className="rounded-full text-xs whitespace-nowrap sm:text-sm"
          >
            <Link to={MARKETING_SIGNUP_PATH}>Get started</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

/** "Products" disclosure: a panel of every product with its status. A real
 * button (works by keyboard and touch, not hover-only); closes on Escape, on
 * outside click and when a product is chosen. */
function ProductsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node))
        setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="text-sm"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        Products
        <ChevronDown
          className={`size-4 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </Button>
      {open ? (
        <div className="absolute top-full left-0 z-50 mt-2 w-[26rem] rounded-2xl border border-[var(--border)] bg-[var(--surface-solid)] p-2 shadow-lg">
          <ul className="grid list-none gap-0.5 p-0">
            {PRODUCTS.map((product) => {
              const Icon = getIcon(product.icon as IconName);
              return (
                <li key={product.slug}>
                  <Link
                    to="/products/$slug"
                    params={{ slug: product.slug }}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-3 rounded-xl p-3 transition hover:bg-[var(--muted)]"
                  >
                    <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--action)] text-[var(--action-foreground)]">
                      <Icon className="size-4" aria-hidden="true" />
                    </span>
                    <span className="grid gap-0.5">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        {product.name}
                        <span className="text-xs font-normal text-[var(--muted-foreground)]">
                          {STATUS_LABEL[product.status]}
                        </span>
                      </span>
                      <span className="text-xs text-[var(--muted-foreground)]">
                        {product.tagline}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="mt-1 border-t border-[var(--border)] p-1 pt-2">
            <Link
              to="/products"
              onClick={() => setOpen(false)}
              className="block rounded-lg px-3 py-2 text-sm font-medium hover:bg-[var(--muted)]"
            >
              See all products
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
